'use client';

import { createContext, Fragment, useContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { InventoryFiltersRange } from '@/components/Inventory/InventoryFilters/InventoryFiltersRange';
import {
  mobileFiltersClass,
  RANGE_FILTER_SECTIONS,
  SEARCHABLE_FILTER_SECTIONS,
  SORT_BY_SECTION,
} from '@/constants/inventory';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { useFiltersList } from '@/hooks/useFiltersList';
import { hasFilters, isFilterSection } from '@/tools/inventory';
import { FilterSection, FilterValue, SortBy } from '@/types/inventory';
import { faSlidersSimple } from '@fortawesome/pro-regular-svg-icons';
import { faSearch, faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FilterSections, FilterSectionsSectionComponent, FilterSectionsTagComponent } from './FilterSections';
import { getCombinedAndSortedOptions } from './utils';

interface MobileTabletFiltersContentContextData {
  selectedSection: string;
  setSelectedSection: (section: string) => void;
}

const MobileTabletFiltersContentContext = createContext({} as MobileTabletFiltersContentContextData);

interface SectionButtonProps {
  amount: number;
  section: string;
}

const SectionButton = ({ section, amount }: SectionButtonProps) => {
  const { selectedSection, setSelectedSection } = useContext(MobileTabletFiltersContentContext);

  return (
    <li className={selectedSection === section ? '' : 'border-r'}>
      <button
        className={twMerge(
          'p-4 text-sm md:text-base w-full flex items-center justify-between gap-2',
          selectedSection === section ? 'border-l-8 border-blue-500 text-blue-500 font-bold' : 'pl-6 bg-blue-50',
        )}
        type="button"
        onClick={() => setSelectedSection(section)}
        data-testid={`mobile-filter-section-${section}`}
      >
        <span className="whitespace-nowrap">{section}</span>
        {amount > 0 && <span className="font-medium text-black-400">{amount}</span>}
      </button>
    </li>
  );
};

interface SingleOptionProps {
  option: string;
  onSelectOption: (option: string) => void;
  isSelected: boolean;
  className?: string;
  buttonClassName?: string;
}

const SingleOption = ({ option, onSelectOption, isSelected, className, buttonClassName }: SingleOptionProps) => (
  <li className={className}>
    <button
      type="button"
      className={twMerge(
        'py-2 px-2.5 rounded-md border border-black-100 bg-white text-sm md:text-base',
        isSelected && 'bg-blue-50 border-blue-200 text-blue-700',
        buttonClassName,
      )}
      onClick={() => onSelectOption(option)}
      data-testid={`mobile-filter-option-${option}`}
    >
      {option}
    </button>
  </li>
);

interface OptionsSectionContentProps {
  section: string;
  onSelectOption: (option: string) => void;
  selectedOptions: FilterValue;
}

const OptionsSectionContent = ({ section, onSelectOption, selectedOptions }: OptionsSectionContentProps) => {
  const [search, setSearch] = useState<string>('');
  const { baseTagFilters, tagFilters, toggleTagFilter, getSectionOptions, addFilterOptions, removeFilterOptions } =
    useContext(NewInventoryFiltersContext);

  if (!(selectedOptions instanceof Array)) return null;

  const lowerSearch = search.toLowerCase().trim();
  const options = getSectionOptions(section);
  const tagOptions = baseTagFilters.find((item) => item.name === section)?.values || [];
  const tagValue = tagFilters[section] || [];

  // Combine and sort options using the shared utility
  const combinedOptions = getCombinedAndSortedOptions(section, options, tagOptions);

  const selectedOptionsElements: JSX.Element[] = [];
  const unselectedOptionsElements: JSX.Element[] = [];

  for (const option of combinedOptions) {
    if (lowerSearch && !(typeof option === 'string' ? option : option.label).toLowerCase().includes(lowerSearch))
      continue;

    if (typeof option === 'string') {
      const isSelected = selectedOptions.includes(option);
      const optionElement = (
        <SingleOption
          key={option}
          option={option}
          onSelectOption={() => onSelectOption(option)}
          isSelected={isSelected}
        />
      );

      if (isSelected) selectedOptionsElements.push(optionElement);
      else unselectedOptionsElements.push(optionElement);
      continue;
    }

    // Handle tag options
    if ('isTag' in option) {
      const isSelected = tagValue.includes(option.label);
      const optionElement = (
        <SingleOption
          key={option.label}
          option={option.label}
          onSelectOption={() => toggleTagFilter(section, option.label)}
          isSelected={isSelected}
        />
      );

      if (isSelected) selectedOptionsElements.push(optionElement);
      else unselectedOptionsElements.push(optionElement);
      continue;
    }

    // Handle grouped options
    const isAllSubOptionsSelected = option.options.every((subOption) => selectedOptions.includes(subOption));

    const optionElement = (
      <SingleOption
        key={option.label}
        option={option.label}
        onSelectOption={() => {
          if (!isFilterSection(section)) return;

          if (isAllSubOptionsSelected) {
            removeFilterOptions(section, option.options);
          } else {
            addFilterOptions(section, option.options);
          }
        }}
        isSelected={isAllSubOptionsSelected}
        className="w-full"
        buttonClassName="font-medium"
      />
    );

    if (isAllSubOptionsSelected) selectedOptionsElements.push(optionElement);
    else unselectedOptionsElements.push(optionElement);

    for (const subOption of option.options) {
      const isSelected = selectedOptions.includes(subOption);
      const subOptionElement = (
        <SingleOption
          key={subOption}
          option={subOption}
          onSelectOption={() => onSelectOption(subOption)}
          isSelected={isSelected}
        />
      );

      if (isSelected) selectedOptionsElements.push(subOptionElement);
      else unselectedOptionsElements.push(subOptionElement);
    }
  }

  return (
    <Fragment>
      {SEARCHABLE_FILTER_SECTIONS.has(section) && (
        <div className="bg-blue-50 rounded relative overflow-hidden">
          <FontAwesomeIcon
            icon={faSearch}
            className="size-3 text-black-350 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            type="text"
            className="py-3.5 pl-10 pr-2 size-full placeholder-black-350 text-black-500 border-none bg-transparent text-sm md:text-base focus:outline-none focus:ring-0 focus:border-none"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`mobile-filter-search-${section}`}
          />
        </div>
      )}
      <div className="flex-1 relative">
        <ul className="flex items-start flex-wrap gap-x-2.5 gap-y-3 absolute top-0 left-0 w-full max-h-full overflow-y-auto pb-4">
          {selectedOptionsElements}
          {unselectedOptionsElements}
        </ul>
      </div>
    </Fragment>
  );
};

const SectionComponent: FilterSectionsSectionComponent = ({ section }) => {
  const { filters, tagFilters } = useContext(NewInventoryFiltersContext);

  const filterValue = filters[section];

  let amount = 0;
  if (filterValue) {
    amount = 1;
    if (filterValue instanceof Array) amount = filterValue.length;
  }
  if (tagFilters[section]) amount += tagFilters[section].length;

  return <SectionButton section={section} amount={amount} />;
};

const TagComponent: FilterSectionsTagComponent = ({ item }) => {
  const { tagFilters } = useContext(NewInventoryFiltersContext);

  return <SectionButton section={item.name} amount={tagFilters[item.name]?.length || 0} />;
};

interface MobileTabletFiltersContentProps {
  onClose: () => void;
  total?: number;
}

const MobileTabletFiltersContent = ({ onClose, total }: MobileTabletFiltersContentProps) => {
  const [selectedSection, setSelectedSection] = useState<string>(SORT_BY_SECTION);
  const { filters, clearFilters, sortBy, toggleFilter, setSortBy, tagFilters } = useContext(NewInventoryFiltersContext);

  const isRangeSection = RANGE_FILTER_SECTIONS.has(selectedSection as FilterSection);

  const hasMobileFilters =
    sortBy !== SortBy.RECOMMENDED ||
    Object.values(filters).some(hasFilters) ||
    Object.values(tagFilters).some((tags) => tags.length > 0);

  return (
    <MobileTabletFiltersContentContext.Provider value={{ selectedSection, setSelectedSection }}>
      <div className="fixed inset-0 z-50 bg-white flex flex-col text-black-500 font-helvetica">
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-black-100">
          <h2 className="font-medium text-xl">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-black-500 hover:text-black-600"
            data-testid="close-filters-button"
          >
            <FontAwesomeIcon icon={faX} className="size-4" />
          </button>
        </div>
        <div className="flex-1 flex">
          <div className="w-32 min-[380px]:w-36 xs:w-40 relative">
            <ul className="flex flex-col divide-y divide-black-100 absolute top-0 left-0 size-full overflow-y-auto">
              <SectionButton section={SORT_BY_SECTION} amount={sortBy === SortBy.RECOMMENDED ? 0 : 1} />

              <FilterSections sectionComponent={SectionComponent} tagComponent={TagComponent} />
            </ul>
          </div>

          <div
            className="flex-1 flex flex-col gap-3.5 pt-3 pb-4 px-5 min-[380px]:px-6"
            data-testid={`mobile-filter-range-${selectedSection}`}
          >
            <h3 className="font-bold" data-testid={`${selectedSection}-h3`}>
              {selectedSection}
            </h3>

            {!isRangeSection && (
              <OptionsSectionContent
                section={selectedSection}
                key={selectedSection}
                selectedOptions={
                  selectedSection === SORT_BY_SECTION
                    ? [sortBy]
                    : (isFilterSection(selectedSection) && filters[selectedSection]) || []
                }
                onSelectOption={(option) => {
                  if (isFilterSection(selectedSection)) {
                    toggleFilter(selectedSection, option);
                    return;
                  }

                  if (selectedSection === SORT_BY_SECTION) {
                    setSortBy(option as SortBy);
                  }
                }}
              />
            )}

            {isRangeSection && isFilterSection(selectedSection) && (
              <InventoryFiltersRange className="max-w-64" section={selectedSection} key={selectedSection} />
            )}
          </div>
        </div>
        <div className="flex justify-between px-5 min-[380px]:px-6 py-3.5 border-t border-black-100">
          <div>
            {hasMobileFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="py-2 px-4 min-[380px]:px-6 text-blue-500 border border-blue-400 rounded-md hover:bg-blue-50"
              >
                Clear Filters
              </button>
            )}
          </div>
          <button
            type="button"
            className="py-2 px-4 min-[380px]:px-6 text-white bg-blue-500 border border-blue-500 rounded-md hover:bg-blue-600 hover:border-blue-600"
            onClick={onClose}
            data-testid="mobile-show-results-button"
          >
            {total ? `Show ${total} results` : 'Show results'}
          </button>
        </div>
      </div>
    </MobileTabletFiltersContentContext.Provider>
  );
};

const FilterList = () => {
  const { filtersList } = useFiltersList({ includeSort: true });
  const { clearFilters } = useContext(NewInventoryFiltersContext);

  if (filtersList.length === 0) return null;

  return (
    <div className="flex flex-col gap-6 px-8 md:px-10 pt-8 -mb-5">
      <ul className="flex gap-2.5 flex-wrap">
        {filtersList.map((item) => (
          <li
            key={item.key}
            className="flex items-center gap-0.5 py-1.5 px-2 rounded-md border border-black-200"
            data-testid={`mobile-filter-tag-${item.label}`}
          >
            <span className="text-sm text-blue-700">{item.label}</span>
            <button type="button" onClick={item.onRemove} data-testid={`mobile-close-filter-tag-${item.label}`}>
              <FontAwesomeIcon icon={faX} className="size-3" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="underline text-xs self-end"
        onClick={clearFilters}
        data-testid="mobile-clear-filters-main-button"
      >
        Clear Filters
      </button>
    </div>
  );
};

interface MobileTabletFiltersProps {
  total?: number | null;
}

export const MobileTabletFilters = ({ total }: MobileTabletFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Fragment>
      <div className="-mx-3 md:-mx-10 -mt-6 border-b border-black-100 bg-white z-40 top-0 left-0 right-0 sticky lg:hidden">
        <div className="flex items-center justify-between py-4 px-8 md:px-10">
          <div className="flex gap-1.5 font-helvetica text-black-500 text-sm md:text-base">
            {typeof total === 'number' && (
              <Fragment>
                <span data-testid="total-results-mobile">{total}</span>
                <span className="text-black-350">results</span>
              </Fragment>
            )}
          </div>
          <button
            type="button"
            className={`flex items-center gap-1.5 text-sm md:text-base px-4 py-1.5 border border-black-200 rounded-full hover:bg-black-50 ${mobileFiltersClass}`}
            onClick={() => setIsOpen(true)}
            data-testid="mobile-filters-button"
          >
            Filters
            <FontAwesomeIcon icon={faSlidersSimple} className="size-4" />
          </button>
        </div>
      </div>
      <div className="-mx-3 md:-mx-10 -mt-6 py-4 lg:hidden">
        <FilterList />
        {isOpen && <MobileTabletFiltersContent total={total} onClose={() => setIsOpen(false)} />}
      </div>
    </Fragment>
  );
};
